<docs>
  # UiKit - Animation
  > *generic way to create cool animations -> REUSABLE !
</docs>
  
<script lang="ts">
import { computed, defineComponent, PropType, ref, toRefs } from 'vue';
import { templateRef, until, useIntersectionObserver, debouncedWatch } from '@vueuse/core';
import { tryOnUnmounted } from '@vueuse/shared';
import interfaceStore from 'store/interface';
import scrollStore from 'components/SmoothScroll/store';
import useFlow from 'utils/flow';

const UiAnimation = defineComponent({
  name: "UiAnimation",
  props: {
    /**
      * Force a specific tag on the element. (semantic purpose)
    */
    tag: {
      type: String,
      default: 'div'
    },
    /**
      * Animation name from type AnimationOpt
    */
    animation: {
      type: String as PropType<AnimationOpt>,
      default: 'opacity'
    },
    /**
      * autoplay animation on scroll
    */
    auto: {
      type: Boolean,
      default: true,
    },
    /**
      * repeat animation if auto (when element enter/leave viewport)
    */
    autoRepeat: {
      type: Boolean,
      default: true,
    },
    /**
      * repeat animation if auto only when scrolling top
    */
    autoTop: {
      type: Boolean,
      default: true,
    }
  },
  setup(props){
    const el = templateRef<HTMLElement>('el');
    const anim = ref<any>();

    const { scrollable } = toRefs(scrollStore.state);
    const { viewport } = toRefs(interfaceStore.state);

    const { onInitUi } = useFlow();

    const played = ref<boolean>(false);
    
    onInitUi( () => {
      // load corresponding animation file
      if (props.animation) {
        import(`./animations/${props.animation}.ts`)
        .then((module) => {
          return anim.value = new module.default({
            el: el.value, 
          });
        })
        // make sure loader is done.
        .then( async () => {
          return await until(scrollable).toBe(true);
        })
        // if auto anim start watcher
        .then(() => {
          if (props.auto) {
            return createWatcher();
          }
        })
      }
    })

    const createWatcher = () => {
      useIntersectionObserver(
        el,
        ([entry]) => {
          let fromTop = true
          if (props.autoTop) {
            fromTop = entry.boundingClientRect.y > 0
          } 
          // entry.boundingClientRect.y > 0 -> give us the possibility to trigger events only when scrolling from top to bottom
          if (entry.isIntersecting && fromTop) {
            if (!props.autoRepeat && played.value) return;
            enter();
            played.value = true;
          } else if (!entry.isIntersecting && props.autoRepeat && fromTop){
            leave();
            played.value = false;
          }
        },
        {
          threshold: 0
        }
      )

      debouncedWatch(
        () => viewport.value.width,
        () => {
          anim.value?.update();
        },
        { debounce: 500, deep: true }
      )
    }

    tryOnUnmounted( () => {
      anim.value?.dispose();
    })

    const enter = () => {
      anim.value?.enter();
    }
    const leave = () => {
      anim.value?.leave();
    }

    const tl = computed(()=>{
      return anim.value?.tl;
    });

    return {
      tl: tl,
      played,
      // functions
      enter: enter,
      leave: leave,

    }
  }
});
export default UiAnimation
</script>
  
<template>
  <component :is="tag"
             ref="el"
             :data-played="played"
             :data-animation="animation"
  >
    <slot />
  </component>
</template>
  
<style lang="stylus" scoped>
  [data-animation="zigzag"]
    overflow hidden

  [data-animation="astro"]
    :deep(.line)
      overflow hidden
    :deep(.char)
      display inline-block
      position relative

  [data-animation="hero"]
  [data-animation="sylvestre"]
    :deep(.line)
      overflow hidden
    :deep(.char)
      display inline-block
      position relative

  [data-animation="cartier"]
    :deep(.line)
      perspective 400px
      perspective-origin bottom
      line-height 1
    :deep(.word)
      transform-origin center bottom

  [data-animation="getRepeat"]
    text-transform uppercase
    :deep(.line)
      overflow hidden
    :deep(.word)
      transform-origin left bottom

</style>
  